import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

interface Stats {
    totalProperties: number;
    activeProperties: number;
    featuredProperties: number;
    forRent: number;
    forSale: number;
    featuredRemaining: number;
}

export default function Dashboard() {
    const { user, token } = useAuth();
    const [stats, setStats] = useState<Stats | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (token) {
            api.getStats(token)
                .then(data => {
                    if (!data.message) setStats(data);
                })
                .catch(console.error)
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, [token]);

    return (
        <UserDashboardLayout>
            <div className="mb-8">
                <h2 className="text-3xl font-black text-slate-800">Welcome To Your Account!</h2>
                <p className="text-blue-950 font-medium">Hello, <span className="text-blue-600 font-bold">{user?.name}</span>! Here's your property overview.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                <div className="bg-white p-6 rounded-3xl border border-blue-900/10 shadow-sm flex items-center gap-6">
                    <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center">
                        <i className="fa-solid fa-house-chimney text-2xl"></i>
                    </div>
                    <div>
                        <h4 className="text-2xl font-black text-slate-800">{stats?.totalProperties ?? 0}</h4>
                        <span className="text-xs font-bold text-blue-950 uppercase tracking-widest">Total Properties</span>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-3xl border border-blue-900/10 shadow-sm flex items-center gap-6">
                    <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center">
                        <i className="fa-solid fa-check-double text-2xl"></i>
                    </div>
                    <div>
                        <h4 className="text-2xl font-black text-slate-800">{stats?.activeProperties ?? 0}</h4>
                        <span className="text-xs font-bold text-blue-950 uppercase tracking-widest">Active Listings</span>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-3xl border border-blue-900/10 shadow-sm flex items-center gap-6">
                    <div className="w-14 h-14 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center">
                        <i className="fa-solid fa-star text-2xl"></i>
                    </div>
                    <div>
                        <h4 className="text-2xl font-black text-slate-800">{stats?.featuredProperties ?? 0}</h4>
                        <span className="text-xs font-bold text-blue-950 uppercase tracking-widest">Featured</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                <div className="bg-white p-6 rounded-3xl border border-blue-900/10 shadow-sm flex items-center gap-6">
                    <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center">
                        <i className="fa-solid fa-key text-2xl"></i>
                    </div>
                    <div>
                        <h4 className="text-2xl font-black text-slate-800">{stats?.forRent ?? 0}</h4>
                        <span className="text-xs font-bold text-blue-950 uppercase tracking-widest">For Rent</span>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-3xl border border-blue-900/10 shadow-sm flex items-center gap-6">
                    <div className="w-14 h-14 bg-rose-50 text-rose-600 rounded-2xl flex items-center justify-center">
                        <i className="fa-solid fa-tags text-2xl"></i>
                    </div>
                    <div>
                        <h4 className="text-2xl font-black text-slate-800">{stats?.forSale ?? 0}</h4>
                        <span className="text-xs font-bold text-blue-950 uppercase tracking-widest">For Sale</span>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-3xl border border-blue-900/10 shadow-sm flex items-center gap-6">
                    <div className="w-14 h-14 bg-blue-50 text-blue-950 rounded-2xl flex items-center justify-center">
                        <i className="fa-solid fa-user-shield text-2xl"></i>
                    </div>
                    <div>
                        <h4 className="text-2xl font-black text-slate-800">{stats?.featuredRemaining ?? 0}</h4>
                        <span className="text-xs font-bold text-blue-950 uppercase tracking-widest">Credits Left</span>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-[2rem] border border-blue-900/10 p-10 overflow-hidden shadow-sm">
                <div className="flex justify-between items-center mb-8 pb-4 border-b border-slate-50">
                    <h4 className="text-xl font-bold text-slate-800">Personal Information</h4>
                    <Link to="/my-profile" className="text-sm font-black text-blue-600 uppercase tracking-widest text-[10px]">Edit Details</Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-1">
                        <label className="text-[10px] font-black text-blue-950 uppercase tracking-widest">Full Name</label>
                        <p className="font-bold text-slate-700">{user?.name || '--'}</p>
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-black text-blue-950 uppercase tracking-widest">Email Address</label>
                        <p className="font-bold text-slate-700">{user?.email || '--'}</p>
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-black text-blue-950 uppercase tracking-widest">Phone Number</label>
                        <p className="font-bold text-slate-700">{user?.phone || '--'}</p>
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-black text-blue-950 uppercase tracking-widest">Current City</label>
                        <p className="font-bold text-slate-700">{user?.city || '--'}</p>
                    </div>
                </div>
            </div>
        </UserDashboardLayout>
    );
}


